/** tm/components/CollectNotifyModal.tsx — 자료수집 알림 발송 모달 */
import { useState, useEffect } from 'react';
import { C, T, R, Btn } from './ui';
import * as api from '../api';

interface Recipient { name: string; phone?: string; email?: string; msgType: 'A' | 'B' }

interface Props {
  projectId: string;
  companyName: string;
  ceo?: string; ceoPhone?: string; ceoEmail?: string;
  manager?: string; managerPhone?: string; managerEmail?: string;
  fpName?: string; fpPhone?: string;
  onDone: () => void;
  onCancel: () => void;
  onToast: (msg: string, type?: string) => void;
}

const TYPES = [
  { key: 'initial', label: '최초 안내', desc: '전체 수집 항목 안내' },
  { key: 'reminder', label: '리마인더', desc: '미제출 항목만 안내' },
  { key: 'reject', label: '반려 안내', desc: '반려 항목 + 사유 안내' },
  { key: 'combo', label: '미제출 + 반려', desc: '미제출/반려 항목 동시 안내' },
];

export default function CollectNotifyModal({ projectId, companyName, ceo, ceoPhone, ceoEmail, manager, managerPhone, managerEmail, fpName, fpPhone, onDone, onCancel, onToast }: Props) {
  const [notifyType, setNotifyType] = useState('initial');
  const [msgA, setMsgA] = useState('');
  const [msgB, setMsgB] = useState('');
  const [editedA, setEditedA] = useState('');
  const [editingA, setEditingA] = useState(false);
  const [sendTo, setSendTo] = useState<Record<string, boolean>>({ ceo: !!ceoPhone, manager: !!managerPhone, fp: !!fpPhone });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ sent: number; fail: number; details: { name: string; phone: string; ok: boolean }[] } | null>(null);

  // 메시지 자동 생성
  useEffect(() => {
    api.generateCollectMessage(projectId, notifyType).then(res => {
      const msg = res.message || res.data?.message || '';
      setMsgA(msg);
      if (!editingA) setEditedA(msg);
    }).catch(() => {});

    // B형 (FP용) 메시지 생성
    const portalUrl = `${window.location.origin}/portal/${projectId}`;
    // 이 주소는 대표님이 서류를 올리시는 페이지 — 팀장님이 「대표님께 전달해도 되나요?」 묻지 않게 분명히(2026-10-28 지도사)
    setMsgB(`[임지도사] ${companyName} 자료수집 안내\n\n안녕하세요.\n${companyName} 건으로 필요서류 제출을 요청드렸습니다.\n\n▶ 대표님께 그대로 전달해 주세요\n· 필요서류 제출 ${portalUrl}\n\n대표님이 직접 서류를 올리시는 페이지이고, 같은 주소에서 제출 현황도 보실 수 있습니다.`);
  }, [projectId, notifyType, companyName]);

  function buildRecipients(): Recipient[] {
    const r: Recipient[] = [];
    if (sendTo.ceo && ceo) r.push({ name: ceo + ' 대표', phone: ceoPhone, email: ceoEmail, msgType: 'A' });
    if (sendTo.manager && manager) r.push({ name: manager, phone: managerPhone, email: managerEmail, msgType: 'A' });
    if (sendTo.fp && fpName) r.push({ name: fpName + ' FP', phone: fpPhone, msgType: 'B' });
    return r;
  }

  async function handleSend() {
    const recipients = buildRecipients();
    if (recipients.length === 0) { onToast('수신자를 선택하세요', 'error'); return; }
    setSending(true);
    try {
      const res = await api.sendCollectNotification({
        projectId,
        type: notifyType,
        recipients,
        messageSms: editedA || msgA,
      });
      const results = res.results || res.data?.results || [];
      const sent = results.filter((r: Record<string, unknown>) => r.smsOk || r.emailOk).length;
      const fail = results.length - sent;
      setResult({ sent, fail, details: results.map((r: Record<string, unknown>) => ({
        name: (r.name as string) || '',
        phone: (r.phone as string) || '',
        ok: !!(r.smsOk || r.emailOk),
      })) });
      onToast(`${sent}건 발송 완료${fail > 0 ? `, ${fail}건 실패` : ''}`);
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '발송 실패', 'error');
    }
    setSending(false);
  }

  const recipients = buildRecipients();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }} onClick={onCancel} />
      <div style={{ position: 'relative', width: '100%', maxWidth: 560, maxHeight: '92vh', borderRadius: 16, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>

        {/* 헤더 */}
        <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #111827, #1B3A5C)', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>📨 자료수집 알림 발송</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>{companyName}</div>
          </div>
          <button onClick={onCancel} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', color: '#fff', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {result ? (
            /* 발송 완료 결과 */
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{result.fail === 0 ? '✅' : '⚠️'}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: result.fail === 0 ? C.green : C.orange, marginBottom: 4 }}>
                {result.sent}건 발송 완료{result.fail > 0 ? `, ${result.fail}건 실패` : ''}
              </div>
              <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 12 }}>고객과 FP에게 자료수집 안내가 발송되었습니다</div>
              {result.details && result.details.length > 0 && (
                <div style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 8, background: '#F8FAFC', border: `1px solid ${C.border}` }}>
                  {result.details.map((d, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < result.details.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                      <span style={{ fontSize: T.xs, fontWeight: 600 }}>{d.name || '-'} <span style={{ color: C.muted, fontWeight: 400 }}>{d.phone}</span></span>
                      <span style={{ fontSize: T.xs, fontWeight: 600, color: d.ok ? C.green : C.red }}>{d.ok ? '\u2705 \uc131\uacf5' : '\u274c \uc2e4\ud328'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* 알림 유형 — 드롭다운 */}
              <div style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 6 }}>알림 유형</div>
              <select value={notifyType} onChange={e => { setNotifyType(e.target.value); setEditingA(false); }}
                style={{ width: '100%', padding: '8px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', marginBottom: 16, background: '#fff' }}>
                {TYPES.map(t => <option key={t.key} value={t.key}>{t.label} — {t.desc}</option>)}
              </select>

              {/* 수신자 */}
              <div style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 6 }}>수신자</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                {ceo && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: T.xs, cursor: 'pointer' }}>
                    <input type="checkbox" checked={sendTo.ceo} onChange={e => setSendTo(p => ({ ...p, ceo: e.target.checked }))} style={{ accentColor: C.accent }} />
                    <span>{ceo} 대표</span>
                    {ceoPhone && <span style={{ color: C.muted }}>({ceoPhone})</span>}
                    <span style={{ fontSize: 9, color: C.accent, fontWeight: 600 }}>A형</span>
                  </label>
                )}
                {manager && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: T.xs, cursor: 'pointer' }}>
                    <input type="checkbox" checked={sendTo.manager} onChange={e => setSendTo(p => ({ ...p, manager: e.target.checked }))} style={{ accentColor: C.accent }} />
                    <span>{manager}</span>
                    {managerPhone && <span style={{ color: C.muted }}>({managerPhone})</span>}
                    <span style={{ fontSize: 9, color: C.accent, fontWeight: 600 }}>A형</span>
                  </label>
                )}
                {fpName && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: T.xs, cursor: 'pointer' }}>
                    <input type="checkbox" checked={sendTo.fp} onChange={e => setSendTo(p => ({ ...p, fp: e.target.checked }))} style={{ accentColor: '#7C3AED' }} />
                    <span>{fpName} FP</span>
                    {fpPhone && <span style={{ color: C.muted }}>({fpPhone})</span>}
                    <span style={{ fontSize: 9, color: '#7C3AED', fontWeight: 600 }}>B형</span>
                  </label>
                )}
                {!ceo && !manager && !fpName && <div style={{ fontSize: T.xs, color: C.muted }}>수신자 정보가 없습니다</div>}
              </div>

              {/* A형 메시지 미리보기 */}
              <div style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 6 }}>
                A형 메시지 (고객용)
                <button onClick={() => setEditingA(!editingA)} style={{ marginLeft: 8, padding: '1px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', fontSize: 10, color: C.sub, cursor: 'pointer' }}>
                  {editingA ? '미리보기' : '수정'}
                </button>
              </div>
              {editingA ? (
                <textarea value={editedA} onChange={e => setEditedA(e.target.value)} rows={6}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, fontSize: T.sm, lineHeight: 1.6, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 12 }} />
              ) : (
                <div style={{ padding: '10px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.bgAlt, fontSize: T.sm, lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 12, maxHeight: 150, overflowY: 'auto' }}>
                  {editedA || msgA || '메시지 생성 중...'}
                </div>
              )}

              {/* B형 메시지 미리보기 */}
              {sendTo.fp && fpName && (
                <>
                  <div style={{ fontSize: T.xs, fontWeight: 600, color: '#7C3AED', marginBottom: 6 }}>B형 메시지 (FP용)</div>
                  <div style={{ padding: '10px 12px', borderRadius: R.md, border: '1px solid #7C3AED33', background: '#F5F3FF', fontSize: T.sm, lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 12, maxHeight: 120, overflowY: 'auto' }}>
                    {msgB || '메시지 생성 중...'}
                  </div>
                </>
              )}

              {/* 발송 요약 */}
              {recipients.length > 0 && (
                <div style={{ padding: '8px 12px', borderRadius: R.md, background: C.bgAlt, border: `1px solid ${C.border}`, fontSize: T.xs, color: C.sub, marginBottom: 8 }}>
                  📨 {recipients.map(r => r.name).join(', ')}에게 SMS 발송
                </div>
              )}
            </>
          )}
        </div>

        {/* 하단 */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8 }}>
          {result ? (
            <Btn onClick={onDone} style={{ flex: 1 }}>확인</Btn>
          ) : (
            <>
              <Btn variant="secondary" onClick={onCancel} style={{ flex: 1 }}>나중에</Btn>
              <Btn onClick={handleSend} disabled={sending || recipients.length === 0}
                style={{ flex: 2, opacity: (sending || recipients.length === 0) ? 0.5 : 1 }}>
                {sending ? '발송 중...' : `📨 ${recipients.length}건 발송하기`}
              </Btn>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
